import React, { useState } from "react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling, usePrice } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard, Pill } from "@/components/croc/ui";
import { fmtUsd, fmtNum, pct } from "@/lib/format";
import api from "@/lib/api";
import { Wallet, Flame, AlertTriangle, Gauge, Timer } from "lucide-react";
import { RewardsHistory } from "@/components/croc/RewardsHistory";
import { PayoutCountdown } from "@/components/croc/PayoutCountdown";
import { RestakeNotice } from "@/components/croc/RestakeNotice";

const PERCENTS = [25, 50, 75, 100];

export default function Withdrawals() {
  const { address, user, refreshUser, signAction } = useWallet();
  const price = usePrice();
  const { data: metrics } = usePolling(() => api.metrics(), [], 20000);
  const { data: cfg } = usePolling(() => api.config(), [], 60000);
  const roiTime = cfg?.roi_payout_gst || "00:00";
  const [busy, setBusy] = useState(false);
  const [busyPrincipal, setBusyPrincipal] = useState(false);
  const [last, setLast] = useState(null);

  const available = user?.claimable ?? 0;
  const principalAvail = user?.principal_claimable ?? 0;
  const accruing = user?.pending_roi ?? 0;

  const withdraw = async (percent) => {
    if (available <= 0) return toast.error(`Nothing available — ROI is credited daily at ${roiTime} GST`);
    try {
      setBusy(true);
      const sig = await signAction("withdraw");
      const res = await api.withdraw({ address, percent, ...sig });
      setLast({ percent, ...res });
      toast.success(`Withdrew ${fmtUsd(res.net)} ${res.token || "USDT"}`, {
        description: `10% fee ${fmtUsd(res.fee)} · sent to your wallet`,
      });
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setBusy(false);
    }
  };

  const withdrawPrincipal = async () => {
    if (principalAvail <= 0) return toast.error("No principal available to withdraw");
    try {
      setBusyPrincipal(true);
      const sig = await signAction("withdraw");
      const res = await api.withdrawPrincipal(address, sig);
      toast.success(`Principal withdrawn: ${fmtUsd(res.net)} ${res.token || "USDT"}`, {
        description: "0% fee — 100% of your principal sent to your wallet",
      });
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Principal withdrawal failed");
    } finally {
      setBusyPrincipal(false);
    }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Withdrawals & Revenue Sustainability"
        title="Withdraw USDT"
        subtitle={`ROI is credited daily at ${roiTime} GST (UAE) into your withdrawable balance. Withdraw 25–100% with a 10% admin fee — paid straight to your wallet in USDT, no CROC burn.`}
        testid="withdraw-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Withdrawable Income" value={fmtUsd(available)} icon={Wallet} accent="emerald" testid="withdraw-available" />
        <StatCard label="Principal Balance (0% fee)" value={fmtUsd(principalAvail)} icon={Wallet} accent="gold" testid="withdraw-principal-balance" />
        <StatCard label={`Accruing (credits ${roiTime} GST)`} value={fmtUsd(accruing)} sub={user?.projected_daily_roi ? `≈ ${fmtUsd(user.projected_daily_roi)}/day` : undefined} icon={Timer} accent="gold" testid="withdraw-accruing" />
        <StatCard label="Live Rate" value={fmtUsd(price?.price, 4)} icon={Timer} accent="gold" testid="withdraw-rate" />
      </div>

      {/* fee-free principal withdrawal */}
      <Panel glow="gold" className="mb-6" data-testid="principal-withdraw-panel">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 grid place-items-center shrink-0">
              <Wallet className="w-5 h-5 gold-text" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-white">Withdraw Principal</span>
                <Pill tone="gold" className="text-[10px]">0% Fee — 100% Paid</Pill>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your matured stake principal (e.g. completed 24h Flash Snap) — the full {fmtUsd(principalAvail)} goes straight to your wallet, no deduction. Fees apply to income only.
              </p>
            </div>
          </div>
          <button
            data-testid="withdraw-principal-button"
            disabled={busyPrincipal || principalAvail <= 0}
            onClick={withdrawPrincipal}
            className="rounded-xl bg-[#1CFD4C] px-5 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {busyPrincipal ? "Withdrawing…" : `Withdraw ${fmtUsd(principalAvail)}`}
          </button>
        </div>
      </Panel>

      <div className="mb-6"><PayoutCountdown /></div>

      <RestakeNotice user={user} />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" glow="gold" data-testid="withdraw-panel">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Select withdrawal amount</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {PERCENTS.map((p) => {
              const gross = available * p / 100;
              const net = gross * 0.9;
              return (
                <button
                  key={p}
                  data-testid={`withdraw-${p}-percent-button`}
                  disabled={busy || available <= 0}
                  onClick={() => withdraw(p)}
                  className="croc-panel p-4 text-center hover:border-[#1CFD4C]/50 transition-colors disabled:opacity-50"
                >
                  <div className="font-display font-black text-2xl gold-text">{p}%</div>
                  <div className="text-[11px] text-muted-foreground mt-1">net {fmtUsd(net)}</div>
                </button>
              );
            })}
          </div>

          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 gold-text shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              A 10% admin fee applies to INCOME withdrawals only — principal withdrawals are always 0% fee. Reactivation timers: 48h after a 100% exit, 24h for partial withdrawals; positions dormant ≥90 days require re-topup to resume.
            </p>
          </div>

          {last && (
            <div className="mt-4 rounded-xl border border-[#10B981]/25 bg-[#10B981]/[0.05] p-4" data-testid="withdraw-receipt">
              <div className="font-display font-bold text-white mb-2">Receipt · {last.percent}% withdrawal</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Cell label="Gross" value={fmtUsd(last.gross)} />
                <Cell label="Fee (10%)" value={fmtUsd(last.fee)} />
                <Cell label="Net paid" value={fmtUsd(last.net)} accent />
                <Cell label="Token" value={last.token || "USDT"} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Sent to your wallet in {last.token || "USDT"} · reactivation in {last.reactivation_hours}h</div>
            </div>
          )}
        </Panel>

        <Panel data-testid="sustainability-panel">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-4 h-4 emerald-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Sustainability</span>
          </div>
          <div className="space-y-3 text-sm">
            <Line label="TVL" value={fmtUsd(metrics?.tvl)} />
            <Line label="Rewards distributed" value={fmtUsd(metrics?.rewards_distributed)} />
            <Line label="Daily rate (by tier)" value={`${fmtNum((metrics?.daily_rate_min ?? 0) * 100, 2)}–${fmtNum((metrics?.daily_rate_max ?? 0) * 100, 2)}%`} />
            <Line label="Circulating supply" value={`${fmtNum(metrics?.circulating, 0)}`} />
          </div>
          <div className="mt-4 rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Revenue vs Obligations</span>
              <Pill tone={(metrics?.coverage_ratio ?? 0) >= 1 ? "emerald" : "danger"}>
                {(metrics?.scaling_active) ? "Scaling" : "Healthy"}
              </Pill>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              When coverage &lt; 1.0x, distributions auto-scale pro-rata so rewards are never paid from user principal.
            </p>
          </div>
        </Panel>
      </div>

      <div className="mt-6"><PrincipalClaim /></div>

      <div className="mt-6"><RewardsHistory limit={14} title="Your Rewards · incl. Prediction Bonus" /></div>
    </div>
  );
}

function PrincipalClaim() {
  const { address, user, refreshUser } = useWallet();
  const price = usePrice();
  const [busy, setBusy] = useState(null);
  const stakes = (user?.stakes ?? []).filter((s) => !s.principal_claimed);
  const matured = stakes.filter((s) => s.can_claim_principal);

  const claimPrincipal = async (stakeId) => {
    try {
      setBusy(stakeId);
      const res = await api.claimPrincipal(address, stakeId);
      if (res.mode === "usdt") {
        toast.success(`USDT released: ${fmtUsd(res.usdt_out)}`, {
          description: `${fmtNum(res.burned_croc)} CROC burned`,
        });
      } else {
        toast.success(`Principal returned: ${fmtNum(res.croc_out)} CROC`, {
          description: `${fmtUsd(res.principal_usd)} worth at $${fmtNum(res.price, 4)}/CROC`,
        });
      }
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Principal claim failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel data-testid="principal-claim-panel">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="w-4 h-4 gold-text" />
        <span className="font-display font-bold text-white">Claim Staked Principal</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Once a stake completes its lock <b className="text-white">or</b> hits its ROI cap (whichever is first):
        the <b className="gold-text">24h Flash Snap</b> burns the CROC and releases your initial <b className="text-white">USDT</b>;
        all other tiers return your principal as <b className="text-white">CROC</b> at the live rate.
      </p>
      {stakes.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-sm" data-testid="principal-empty">No open stakes.</div>
      ) : (
        <div className="space-y-2">
          {stakes.map((s) => {
            const is24 = s.tier_key === "24h";
            const crocOut = price?.price ? s.principal_usd / price.price : 0;
            const readyLine = is24
              ? `Ready — get ${fmtUsd(s.principal_usd)} USDT back · CROC burned`
              : `Ready — receive ~${fmtNum(crocOut)} CROC`;
            return (
              <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3" data-testid={`principal-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white">{s.tier_name} · {fmtUsd(s.principal_usd)}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {s.can_claim_principal ? readyLine : (s.active ? "Locked — accruing rewards" : "Processing")}
                  </div>
                </div>
                <button
                  data-testid={`claim-principal-${s.id}`}
                  disabled={!s.can_claim_principal || busy === s.id}
                  onClick={() => claimPrincipal(s.id)}
                  className="rounded-xl bg-[#1CFD4C] px-4 py-2 text-sm font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy === s.id ? "Claiming…" : s.can_claim_principal ? "Claim Principal" : "Not matured"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-num font-bold mt-0.5 ${accent ? "emerald-text" : "text-white"}`}>{value}</div>
    </div>
  );
}
function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-num text-white">{value}</span>
    </div>
  );
}
